import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import { ACTUATOR_TYPES } from '../../utils/constants';

const CreateAssociationModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  // ✅ NOUVEAU — association "simple" (2 devices, comportement actuel) ou
  // "avancée" (3 devices : Sensor + Contrôleur IR L5 + Wifi Smart Meter Pro)
  const [advancedMode, setAdvancedMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sensorId: '',
    switchId: '',
    meterId: '',            // ✅ NOUVEAU — 3ᵉ device (Wifi Smart Meter Pro), uniquement en mode avancé
    controlPriority: 'IR_L5', // ✅ NOUVEAU — qui reçoit les commandes ON/OFF (IR_L5 par défaut)
    remoteId: '', // ✅ requis uniquement si l'actuateur choisi est un Contrôleur IR
    autoMode: true,
    scheduleEnabled: false,
    onTime: '06:00',
    offTime: '23:00',
    presenceDelay: 30,
    minOnDuration: 60,
  });

  // ✅ Picker de télécommandes — remplace la saisie manuelle du remoteId :
  // on va chercher la liste réelle des télécommandes rattachées au Contrôleur
  // IR choisi (chez Tuya) et l'utilisateur clique sur celle qu'il veut.
  const [remotes, setRemotes] = useState([]);
  const [remotesLoading, setRemotesLoading] = useState(false);
  const [remotesError, setRemotesError] = useState('');
  const [remoteSearch, setRemoteSearch] = useState('');
  const [selectedRemoteName, setSelectedRemoteName] = useState('');

  // ✅ Un Contrôleur IR (Tuya, catégorie wnykq) a un "double" : Tuya crée aussi
  // un device virtuel par télécommande configurée (ex: "Climatisation"), dont
  // le deviceId EST le remoteId. Ce device virtuel reste un vrai device (visible
  // dans la page Devices, ne pas le supprimer) mais ne doit pas être choisissable
  // ici comme actuateur : on y accède uniquement via Contrôleur IR L5 → picker
  // de télécommande. On détecte ces doublons en interrogeant les télécommandes
  // de chaque Contrôleur IR et en excluant tout device dont le deviceId matche.
  const [hiddenRemoteDeviceIds, setHiddenRemoteDeviceIds] = useState(new Set());

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await api.get('/devices');
      setDevices(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // ✅ Une fois les devices chargés, interroger les télécommandes de chaque
  // Contrôleur IR pour savoir quels autres devices sont en réalité leurs
  // télécommandes virtuelles (à masquer de la liste "Interrupteur").
  useEffect(() => {
    const irHubs = devices.filter(d => d.type === 'ir_controller');
    if (irHubs.length === 0) return;

    let cancelled = false;

    Promise.all(
      irHubs.map(hub =>
        api.get(`/devices/${hub._id}/ir-remotes`)
          .then(res => res.data.data || [])
          .catch(() => []) // un hub sans télécommande ou en erreur ne bloque pas les autres
      )
    ).then((results) => {
      if (cancelled) return;
      const remoteIds = new Set(results.flat().map(r => r.remoteId));
      setHiddenRemoteDeviceIds(remoteIds);
    });

    return () => { cancelled = true; };
  }, [devices]);

  const sensors = devices.filter(d => d.type === 'sensor' && d.online && !d.isAssociated);
  // ✅ Un actuateur peut désormais être un switch classique OU un Contrôleur IR
  // — on exclut les devices virtuels qui représentent en réalité une télécommande
  // déjà accessible via le picker (cf. hiddenRemoteDeviceIds ci-dessus), ET les
  // devices déjà utilisés par une autre association active (isAssociated).
  const actuators = devices.filter(
    d => ACTUATOR_TYPES.includes(d.type) && d.online && !hiddenRemoteDeviceIds.has(d.deviceId) && !d.isAssociated
        && (!advancedMode || d.type === 'ir_controller')
  );

  const selectedActuator = actuators.find(d => d._id === formData.switchId);
  const isIrController = selectedActuator?.type === 'ir_controller';

  // ✅ NOUVEAU — Wifi Smart Meter Pro disponibles pour le 3ᵉ device : des
  // switchs classiques, non déjà associés, distincts de celui choisi comme
  // Interrupteur. Uniquement pertinent en mode avancé + Contrôleur IR choisi.
  const meters = devices.filter(
    d => d.type === 'switch' && d.online && !d.isAssociated && d._id !== formData.switchId
  );

  // ✅ Dès qu'un Contrôleur IR est choisi comme actuateur, on va chercher ses
  // télécommandes chez Tuya — plus besoin de connaître/saisir un remoteId.
  useEffect(() => {
    if (!isIrController || !formData.switchId) {
      setRemotes([]);
      setRemotesError('');
      return;
    }

    let cancelled = false;
    setRemotesLoading(true);
    setRemotesError('');

    api.get(`/devices/${formData.switchId}/ir-remotes`)
      .then((response) => {
        if (cancelled) return;
        setRemotes(response.data.data || []);
      })
      .catch((error) => {
        if (cancelled) return;
        setRemotes([]);
        setRemotesError(
          error.response?.data?.message || 'Impossible de récupérer les télécommandes depuis Tuya'
        );
      })
      .finally(() => {
        if (!cancelled) setRemotesLoading(false);
      });

    return () => { cancelled = true; };
  }, [isIrController, formData.switchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sensorId || !formData.switchId) {
      alert('Veuillez sélectionner un sensor et un équipement à piloter');
      return;
    }

    if (isIrController && !formData.remoteId.trim()) {
      alert('Veuillez saisir le remoteId de la télécommande (ex: climatisation)');
      return;
    }

    if (advancedMode && !formData.meterId) {
      alert('Veuillez sélectionner le Wifi Smart Meter Pro pour une association avancée à 3 devices');
      return;
    }

    setLoading(true);

    try {
      await api.post('/associations', {
        name: formData.name,
        description: formData.description,
        sensorId: formData.sensorId,
        switchId: formData.switchId,
        autoMode: formData.autoMode,
        schedule: {
          enabled: formData.scheduleEnabled,
          onTime: formData.onTime,
          offTime: formData.offTime,
        },
        settings: {
          presenceDelay: formData.presenceDelay,
          minOnDuration: formData.minOnDuration,
        },
        ...(isIrController ? { irConfig: { remoteId: formData.remoteId.trim() } } : {}),
        ...(advancedMode && formData.meterId
          ? { meterId: formData.meterId, controlPriority: formData.controlPriority }
          : {}),
      });

      alert('Association créée avec succès');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="➕ Nouvelle Association Sensor ↔ Switch">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Nom */}
        <Input
          label="Nom de l'association"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Salon - Détection présence"
          required
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (optionnel)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Décrivez l'association..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            rows="3"
          />
        </div>

        {/* ✅ NOUVEAU — Type d'association */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => { setAdvancedMode(false); setFormData({ ...formData, meterId: '' }); }}
            className={`text-left px-4 py-3 rounded-lg border-2 transition ${
              !advancedMode ? 'border-blue-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <p className="font-semibold text-gray-800">Association simple</p>
            <p className="text-xs text-gray-500 mt-0.5">Sensor + 1 équipement (IR L5 ou Smart Meter Pro)</p>
          </button>
          <button
            type="button"
            onClick={() => setAdvancedMode(true)}
            className={`text-left px-4 py-3 rounded-lg border-2 transition ${
              advancedMode ? 'border-blue-primary bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <p className="font-semibold text-gray-800">Association avancée</p>
            <p className="text-xs text-gray-500 mt-0.5">Sensor + IR L5 + Smart Meter Pro (climatisation + consommation)</p>
          </button>
        </div>

        {/* Sélection Sensor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📡 Capteur de présence *
          </label>
          <select
            value={formData.sensorId}
            onChange={(e) => setFormData({ ...formData, sensorId: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            required
          >
            <option value="">-- Sélectionner un sensor --</option>
            {sensors.map((sensor) => (
              <option key={sensor._id} value={sensor._id}>
                {sensor.name} ({sensor.deviceId.substring(0, 8)}...)
              </option>
            ))}
          </select>
          {sensors.length === 0 && (
            <p className="text-sm text-red-600 mt-1">
              Aucun sensor disponible en ligne
            </p>
          )}
        </div>

        {/* Sélection de l'actuateur (Switch classique OU Contrôleur IR) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {advancedMode ? '🌡️ Contrôleur IR L5 *' : '💡 Interrupteur *'}
          </label>
          <select
            value={formData.switchId}
            onChange={(e) => {
              setFormData({ ...formData, switchId: e.target.value, remoteId: '' });
              setSelectedRemoteName('');
              setRemoteSearch('');
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            required
          >
            <option value="">-- Sélectionner --</option>
            {actuators.map((actuator) => (
              <option key={actuator._id} value={actuator._id}>
                {actuator.type === 'ir_controller' ? '🌡️ ' : ''}
                {actuator.name} ({actuator.deviceId.substring(0, 8)}...)
              </option>
            ))}
          </select>
          {actuators.length === 0 && (
            <p className="text-sm text-red-600 mt-1">
              {advancedMode
                ? 'Aucun Contrôleur IR disponible en ligne'
                : 'Aucun switch ou contrôleur IR disponible en ligne'}
            </p>
          )}
        </div>

        {/* ✅ Sélection de la télécommande — remplace la saisie manuelle du remoteId */}
        {isIrController && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🌡️ Télécommande (ex: climatisation) *
            </label>

            {formData.remoteId ? (
              // Une télécommande est sélectionnée : on l'affiche avec une option de changement
              <div className="flex items-center justify-between bg-white border border-orange-300 rounded-lg px-4 py-2">
                <span className="font-medium text-gray-800">
                  🌡️ {selectedRemoteName || formData.remoteId}
                </span>
                <button
                  type="button"
                  onClick={() => { setFormData({ ...formData, remoteId: '' }); setSelectedRemoteName(''); }}
                  className="text-sm text-blue-primary hover:underline"
                >
                  Changer
                </button>
              </div>
            ) : remotesLoading ? (
              <p className="text-sm text-gray-500">Recherche des télécommandes...</p>
            ) : remotesError ? (
              <div>
                <p className="text-sm text-red-600 mb-2">{remotesError}</p>
                {/* Filet de secours : si Tuya ne répond pas, on garde la possibilité de saisir l'ID à la main */}
                <Input
                  label="Saisir manuellement le remoteId (dépannage)"
                  value={formData.remoteId}
                  onChange={(e) => setFormData({ ...formData, remoteId: e.target.value })}
                  placeholder="Ex: bf7d8bd0e386b8690en5po"
                />
              </div>
            ) : remotes.length === 0 ? (
              <p className="text-sm text-red-600">
                Aucune télécommande trouvée sur ce Contrôleur IR. Ajoutez-la d'abord dans l'app Tuya.
              </p>
            ) : (
              <div>
                <input
                  type="text"
                  value={remoteSearch}
                  onChange={(e) => setRemoteSearch(e.target.value)}
                  placeholder="Rechercher une télécommande..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary mb-2"
                />
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y">
                  {remotes
                    .filter((r) => r.remoteName.toLowerCase().includes(remoteSearch.toLowerCase()))
                    .map((r) => (
                      <button
                        type="button"
                        key={r.remoteId}
                        onClick={() => {
                          setFormData({ ...formData, remoteId: r.remoteId });
                          setSelectedRemoteName(r.remoteName);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-orange-100 transition"
                      >
                        🌡️ {r.remoteName}
                      </button>
                    ))}
                  {remotes.filter((r) => r.remoteName.toLowerCase().includes(remoteSearch.toLowerCase())).length === 0 && (
                    <p className="px-4 py-2 text-sm text-gray-500">Aucune télécommande ne correspond à cette recherche.</p>
                  )}
                </div>
              </div>
            )}

            <p className="text-xs text-orange-700 mt-2">
              Liste récupérée directement depuis Tuya — sélectionnez la télécommande de la
              climatisation rattachée à ce Contrôleur IR.
            </p>
          </div>
        )}

        {/* ✅ NOUVEAU — 3ᵉ device (Wifi Smart Meter Pro) + priorité ON/OFF, mode avancé uniquement */}
        {advancedMode && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ⚡ Wifi Smart Meter Pro *
              </label>
              <select
                value={formData.meterId}
                onChange={(e) => setFormData({ ...formData, meterId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                required={advancedMode}
              >
                <option value="">-- Sélectionner un Smart Meter Pro --</option>
                {meters.map((meter) => (
                  <option key={meter._id} value={meter._id}>
                    {meter.name} ({meter.deviceId.substring(0, 8)}...)
                  </option>
                ))}
              </select>
              {meters.length === 0 && (
                <p className="text-sm text-red-600 mt-1">Aucun Wifi Smart Meter Pro disponible en ligne</p>
              )}
              <p className="text-xs text-green-700 mt-1">
                Mesure la consommation électrique de la climatisation, et peut aussi allumer/éteindre.
              </p>
            </div>

            {formData.meterId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Device prioritaire pour les commandes ON/OFF
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, controlPriority: 'IR_L5' })}
                    className={`text-left px-4 py-3 rounded-lg border-2 transition ${
                      formData.controlPriority === 'IR_L5' ? 'border-green-600 bg-white' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-800">🌡️ Contrôleur IR L5</p>
                    <p className="text-xs text-gray-500 mt-0.5">Recommandé — par défaut</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, controlPriority: 'SMART_METER_PRO' })}
                    className={`text-left px-4 py-3 rounded-lg border-2 transition ${
                      formData.controlPriority === 'SMART_METER_PRO' ? 'border-green-600 bg-white' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-800">⚡ Wifi Smart Meter Pro</p>
                    <p className="text-xs text-gray-500 mt-0.5">Coupe l'alimentation directement</p>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Un seul device reçoit les commandes ON/OFF automatiques — l'autre reste disponible
                  pour ses propres capacités (mode/température pour l'IR, consommation pour le Smart Meter Pro).
                  Modifiable ultérieurement depuis les détails de l'association.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Mode automatique */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.autoMode}
              onChange={(e) => setFormData({ ...formData, autoMode: e.target.checked })}
              className="w-4 h-4 text-blue-600"
            />
            <label className="text-sm font-medium text-gray-700">
              🤖 Activer le mode automatique (contrôle par présence)
            </label>
          </div>
        </div>

        {/* Programmation horaire */}
        <div className="border border-gray-300 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <input
              type="checkbox"
              checked={formData.scheduleEnabled}
              onChange={(e) => setFormData({ ...formData, scheduleEnabled: e.target.checked })}
              className="w-4 h-4 text-blue-600"
            />
            <label className="text-sm font-medium text-gray-700">
              📅 Activer la programmation horaire
            </label>
          </div>

          {formData.scheduleEnabled && (
            <div className="grid grid-cols-2 gap-4 mt-3">
              <Input
                label="Heure d'allumage"
                type="time"
                value={formData.onTime}
                onChange={(e) => setFormData({ ...formData, onTime: e.target.value })}
              />
              <Input
                label="Heure d'extinction"
                type="time"
                value={formData.offTime}
                onChange={(e) => setFormData({ ...formData, offTime: e.target.value })}
              />
            </div>
          )}
        </div>

        {/* Paramètres avancés */}
        <div className="border border-gray-300 rounded-lg p-4">
          <h3 className="font-bold mb-3">⚙️ Paramètres avancés</h3>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Délai avant extinction (secondes)
              </label>
              <input
                type="number"
                value={formData.presenceDelay}
                onChange={(e) => setFormData({ ...formData, presenceDelay: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Temps d'attente après disparition de la présence avant d'éteindre
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée minimale d'allumage (secondes)
              </label>
              <input
                type="number"
                value={formData.minOnDuration}
                onChange={(e) => setFormData({ ...formData, minOnDuration: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Durée minimale pendant laquelle le switch reste allumé
              </p>
            </div>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex space-x-3 pt-4">
          <Button type="submit" disabled={loading} className="flex-1">
            {loading ? 'Création...' : '✅ Créer l\'association'}
          </Button>
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Annuler
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateAssociationModal;